import React, { Component, ComponentType, Fragment, ReactNode, createContext } from 'react';
import { TransProps } from 'react-i18next';

import { Notify } from 'components';

interface INotifyArgs {
  duration?: number;
  placement?: 'top' | 'right' | 'bottomLeft';
  component?: ReactNode;
  message?: string;
  i18n?: TransProps;
}

interface IState extends INotifyArgs {
  show: boolean;
  type: 'info' | 'error' | 'infoSolid';
}

export interface IWithNotify {
  notify: {
    info: (props: INotifyArgs) => void;
    infoSolid: (props: INotifyArgs) => void;
    error: (props: INotifyArgs) => void;
  };
}

export interface INotifyComponentProps extends IState {
  show: boolean;
  onClose: () => void;
}

const DEFAULT_NOTIFY_OPTIONS: INotifyArgs = {
  component: null,
  message: null,
  i18n: null,
  duration: 2000,
  placement: 'top',
};

export const NotifyContext = createContext({});

class NotifyProvider extends Component<{}, IState> {
  state: IState = {
    // Internal State
    type: 'info',
    show: false,
    // Options
    ...DEFAULT_NOTIFY_OPTIONS,
  };

  handleClose = () => {
    this.setState({ show: false });
  };

  info = (props: INotifyArgs) => {
    this.setState({
      type: 'info',
      show: true,
      ...DEFAULT_NOTIFY_OPTIONS,
      ...props,
    });
  };

  infoSolid = (props: INotifyArgs) => {
    this.setState({
      type: 'infoSolid',
      show: true,
      ...DEFAULT_NOTIFY_OPTIONS,
      ...props,
    });
  };

  error = (props: INotifyArgs) => {
    this.setState({
      type: 'error',
      show: true,
      ...DEFAULT_NOTIFY_OPTIONS,
      ...props,
    });
  };

  render() {
    const { children } = this.props;

    return (
      <NotifyContext.Provider
        value={{ notify: { info: this.info, infoSolid: this.infoSolid, error: this.error } }}>
        <Fragment>
          <Notify onClose={this.handleClose} {...this.state} />
          {children}
        </Fragment>
      </NotifyContext.Provider>
    );
  }
}

const withNotify = <T extends {}>(ChildComponent: ComponentType<T & IWithNotify>) => (props: T) => (
  <NotifyContext.Consumer>
    {(providerProps: IWithNotify) => <ChildComponent {...props} {...providerProps} />}
  </NotifyContext.Consumer>
);

export { NotifyProvider, withNotify };
